import { useState, ReactNode } from "react";

export type CopyType = "plain" | "rich";

export type Props = {
  /** Callback when copy is triggered */
  onCopy?: () => void;
  /** Text to copy (for plain type) */
  textToCopy?: string;
  /** Copy type - plain text or rich (HTML) */
  type?: CopyType;
  /** Section name to identify element for rich copy */
  sectionName?: string;
  /** Additional CSS classes */
  className?: string;
  /** Render the button */
  renderButton: (props: {
    onClick: () => void;
    copied: boolean;
  }) => ReactNode;
  /** Test ID for testing */
  "data-testid"?: string;
};

/**
 * Base clipboard copy component with no styling.
 * Handles clipboard operations for plain text and rich (HTML) content.
 * Use render props to customize the button appearance.
 *
 * @example
 * // In shared/ui/ClipboardCopyButton.tsx
 * import { ClipboardCopyButtonBase, type Props as ClipboardCopyButtonBaseProps } from "@core/ui";
 *
 * export function ClipboardCopyButton({ label = "Copy", ...props }: ClipboardCopyButtonBaseProps) {
 *   return (
 *     <ClipboardCopyButtonBase
 *       {...props}
 *       renderButton={({ onClick, copied }) => (
 *         <Button onClick={onClick}>
 *           {copied ? "Copied!" : label}
 *         </Button>
 *       )}
 *     />
 *   );
 * }
 */
export function ClipboardCopyButtonBase({
  onCopy,
  textToCopy = "",
  type = "plain",
  sectionName,
  className,
  renderButton,
  "data-testid": testId = "clipboard-copy-button",
}: Props) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    onCopy?.();

    if (type === "plain") {
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      return;
    }

    // Rich text copying with background removal
    if (type === "rich") {
      const elementToCopy = document.querySelector(
        `[data-copy-content="${sectionName}"]`
      );
      if (!elementToCopy) return;

      // Store elements that had background styles
      const parentsWithBg: Array<{
        element: HTMLElement;
        computedBg: string;
        computedBgColor: string;
        originalStyle: {
          background: string;
          backgroundColor: string;
        };
        originalClasses: string[];
      }> = [];

      // Walk up the tree and store/remove background styles
      let currentEl: HTMLElement | null = elementToCopy as HTMLElement;
      while (currentEl) {
        const computedStyle = window.getComputedStyle(currentEl);
        const hasBg =
          computedStyle.background !== "none" ||
          computedStyle.backgroundColor !== "rgba(0, 0, 0, 0)";
        const hasBackgroundClass = Array.from(currentEl.classList).some((cls) =>
          cls.includes("bg-")
        );

        if (hasBg || hasBackgroundClass) {
          parentsWithBg.push({
            element: currentEl,
            computedBg: computedStyle.background,
            computedBgColor: computedStyle.backgroundColor,
            originalStyle: {
              background: currentEl.style.background,
              backgroundColor: currentEl.style.backgroundColor,
            },
            originalClasses: Array.from(currentEl.classList),
          });

          currentEl.style.setProperty("background", "none", "important");
          currentEl.style.setProperty(
            "background-color",
            "transparent",
            "important"
          );

          const classesToRemove = Array.from(currentEl.classList).filter(
            (cls) => cls.includes("bg-")
          );
          currentEl.classList.remove(...classesToRemove);
        }
        currentEl = currentEl.parentElement;
      }

      try {
        const selection = window.getSelection();
        const range = document.createRange();
        range.selectNodeContents(elementToCopy);
        selection?.removeAllRanges();
        selection?.addRange(range);
        document.execCommand("copy");
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error("Failed to copy rich text:", err);
      } finally {
        window.getSelection()?.removeAllRanges();

        // Restore background styles
        parentsWithBg.forEach(
          ({
            element,
            computedBg,
            computedBgColor,
            originalStyle,
            originalClasses,
          }) => {
            element.style.removeProperty("background");
            element.style.removeProperty("background-color");

            if (originalStyle.background)
              element.style.background = originalStyle.background;
            if (originalStyle.backgroundColor)
              element.style.backgroundColor = originalStyle.backgroundColor;

            if (!originalStyle.background && computedBg !== "none") {
              element.style.background = computedBg;
            }
            if (
              !originalStyle.backgroundColor &&
              computedBgColor !== "rgba(0, 0, 0, 0)"
            ) {
              element.style.backgroundColor = computedBgColor;
            }

            element.classList.add(...originalClasses);
          }
        );
      }
    }
  };

  return (
    <span className={className} data-testid={testId}>
      {renderButton({ onClick: handleCopy, copied })}
    </span>
  );
}

ClipboardCopyButtonBase.displayName = "ClipboardCopyButtonBase";

